import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, RotateCcw, Plus, X } from 'lucide-react';
import TacticalCanvasBase from '../components/tactical/TacticalCanvasBase';
import { useSport } from '../contexts/SportContext';
import { BRAND_GREEN, BRAND_GOLD } from '../theme/sucronTheme';


const STORAGE_KEY = 'arenastats_tactics_draft';

const DEFAULT_POSITIONS = {
  football: [
    { id: 'gk', label: 'GK', x: 5, y: 50 },
    { id: 'lb', label: 'LE', x: 20, y: 15 },
    { id: 'cb1', label: 'ZAG', x: 20, y: 38 },
    { id: 'cb2', label: 'ZAG', x: 20, y: 62 },
    { id: 'rb', label: 'LD', x: 20, y: 85 },
    { id: 'cm1', label: 'VOL', x: 40, y: 35 },
    { id: 'cm2', label: 'MEI', x: 40, y: 65 },
    { id: 'lw', label: 'PE', x: 60, y: 15 },
    { id: 'am', label: 'MEI', x: 55, y: 50 },
    { id: 'rw', label: 'PD', x: 60, y: 85 },
    { id: 'st', label: 'ATA', x: 75, y: 50 },
  ],
  football_american: [
    { id: 'qb', label: 'QB', x: 50, y: 50 },
    { id: 'rb', label: 'RB', x: 42, y: 50 },
    { id: 'wr1', label: 'WR1', x: 50, y: 10 },
    { id: 'wr2', label: 'WR2', x: 50, y: 90 },
    { id: 'te', label: 'TE', x: 50, y: 25 },
    { id: 'lt', label: 'LT', x: 48, y: 35 },
    { id: 'lg', label: 'LG', x: 48, y: 42 },
    { id: 'c', label: 'C', x: 48, y: 50 },
    { id: 'rg', label: 'RG', x: 48, y: 58 },
    { id: 'rt', label: 'RT', x: 48, y: 65 },
    { id: 'slot', label: 'SL', x: 50, y: 75 },
  ],
};

const TacticsDesignerPage = () => {
  const navigate = useNavigate();
  const { activeSport } = useSport();
  const sportKey = activeSport === 'football_american' ? 'football_american' : 'football';

  // Load draft from localStorage
  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft.sportKey === sportKey) return draft;
      }
    } catch { /* ignore */ }
    return null;
  };

  const draft = loadDraft();
  const [playName, setPlayName] = useState(draft?.playName || '');
  const [players, setPlayers] = useState(draft?.players || DEFAULT_POSITIONS[sportKey] || []);
  const [checklist, setChecklist] = useState(draft?.checklist || []);
  const [newCheckItem, setNewCheckItem] = useState('');
  const [saving, setSaving] = useState(false);

  // Persist draft to localStorage on every change
  useEffect(() => {
    const data = { sportKey, playName, players, checklist };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [sportKey, playName, players, checklist]);

  const handlePlayerMove = useCallback((id, x, y) => {
    setPlayers((prev) => prev.map((p) => (p.id === id ? { ...p, x, y } : p)));
  }, []);

  const resetPositions = () => {
    setPlayers(DEFAULT_POSITIONS[sportKey] || []);
  };

  const addCheckItem = () => {
    const text = newCheckItem.trim();
    if (!text) return;
    setChecklist((prev) => [...prev, { text, done: false }]);
    setNewCheckItem('');
  };

  const toggleCheck = (idx) => {
    setChecklist((prev) => prev.map((c, i) => (i === idx ? { ...c, done: !c.done } : c)));
  };

  const removeCheck = (idx) => {
    setChecklist((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    // MVP: persist to localStorage only. Backend integration in PROMPT 10.
    const play = {
      id: Date.now().toString(36),
      playName,
      sportKey,
      players,
      checklist,
      createdAt: new Date().toISOString(),
    };
    try {
      const existing = JSON.parse(localStorage.getItem('arenastats_tactics_plays') || '[]');
      existing.push(play);
      localStorage.setItem('arenastats_tactics_plays', JSON.stringify(existing));
      localStorage.removeItem(STORAGE_KEY);
      navigate(`/tactics/plays/${play.id}`);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold" style={{ color: BRAND_GREEN }}>
              Designer Tatico
            </h1>
            <p className="text-gray-500 text-sm">
              {sportKey === 'football_american' ? 'Campo 100x53 yd' : 'Campo 105x68 m'}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resetPositions}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
            >
              <RotateCcw size={14} /> Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !playName.trim()}
              className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              <Save size={14} /> {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Canvas */}
          <div className="lg:col-span-2">
            <TacticalCanvasBase
              sportKey={sportKey}
              players={players}
              paths={[]}
              onPlayerMove={handlePlayerMove}
              width={800}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Play name */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nome da Jogada</label>
              <input
                type="text"
                value={playName}
                onChange={(e) => setPlayName(e.target.value)}
                placeholder="Ex: Spider 2 Y Banana"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-700"
              />
            </div>

            {/* Checklist */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Checklist de Execucao</h3>
              <ul className="space-y-2 mb-3">
                {checklist.map((c, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={c.done}
                      onChange={() => toggleCheck(idx)}
                      className="rounded"
                    />
                    <span className={`text-sm flex-1 ${c.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                      {c.text}
                    </span>
                    <button onClick={() => removeCheck(idx)} className="text-gray-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCheckItem}
                  onChange={(e) => setNewCheckItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addCheckItem()}
                  placeholder="Novo item..."
                  className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  onClick={addCheckItem}
                  className="px-2 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: BRAND_GREEN }}
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Players list */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Jogadores ({players.length})</h3>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {players.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50">
                    <span className="font-medium" style={{ color: BRAND_GREEN }}>{p.label}</span>
                    <span className="text-gray-400 text-xs">
                      ({Math.round(p.x)}, {Math.round(p.y)})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TacticsDesignerPage;
